import React, { useState, useCallback } from "react";
import {
  App,
  Card,
  Typography,
  Steps,
  Alert,
  Space,
  Button,
  Divider,
  List,
} from "antd";
import {
  DownloadOutlined,
  BookOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import "./RavenDownload.css";

const { Title, Paragraph, Text } = Typography;

const ZIP_FILENAME = "raven.zip";

const RavenDownload = () => {
  const { message } = App.useApp();
  const [downloading, setDownloading] = useState(false);
  const base = import.meta.env.BASE_URL || "/";
  const zipUrl = `${String(base).replace(/\/$/, "")}/${ZIP_FILENAME}`;

  const handleDownloadZip = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await fetch(zipUrl, { method: "GET", credentials: "same-origin" });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = ZIP_FILENAME;
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      message.success(`已开始下载 ${ZIP_FILENAME}（ZIP 压缩包）`);
    } catch (err) {
      message.error(
        "压缩包下载失败：" + (err?.message || "请确认静态资源已部署或稍后重试"),
      );
    } finally {
      setDownloading(false);
    }
  }, [zipUrl, message]);

  return (
    <div className="raven-download-page">
      <div className="raven-download-header">
        <Title level={2} className="raven-download-title">
          Raven 交易监控系统
        </Title>
        <Paragraph type="secondary" className="raven-download-sub">
          本地 Python 工具包：成交量监控、自动卖币、每日报告、对冲统计与飞书通知。
          从本页下载压缩包后，在服务器或本机解压使用。
        </Paragraph>
      </div>

      <Card className="raven-download-card" variant="borderless">
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            icon={<SafetyOutlined />}
            message="安全说明"
            description={
              <>
                发布包中<strong>不包含</strong> <Text code>config.toml</Text>
                （若你本地仓库里有该文件，请切勿提交或外发）。请始终从{" "}
                <Text code>config.example.toml</Text> 复制并重命名为{" "}
                <Text code>config.toml</Text> 后填写密钥与账户信息。
              </>
            }
          />

          <div>
            <Title level={4}>下载 ZIP 压缩包</Title>
            <Paragraph>
              点击下方按钮将<strong>以 ZIP 压缩包</strong>形式下载{" "}
              <Text code>{ZIP_FILENAME}</Text>
              （解压后即为完整工具目录）。文件与仓库{" "}
              <Text code>scripts/raven/</Text> 同步，执行{" "}
              <Text code>npm run dev</Text> 或 <Text code>npm run build</Text>{" "}
              / Docker 构建时会自动生成。
            </Paragraph>
            <Button
              type="primary"
              size="large"
              icon={<DownloadOutlined />}
              loading={downloading}
              onClick={handleDownloadZip}
            >
              下载 {ZIP_FILENAME}
            </Button>
          </div>

          <Divider />

          <div>
            <Title level={4}>快速开始</Title>
            <Steps
              direction="vertical"
              size="small"
              current={-1}
              items={[
                {
                  title: "解压并进入目录",
                  description: (
                    <Text type="secondary">
                      将 raven.zip 解压到任意目录，例如{" "}
                      <Text code>/opt/raven</Text>。
                    </Text>
                  ),
                },
                {
                  title: "准备 Python 环境",
                  description: (
                    <Text type="secondary">
                      建议使用 Python 3.10+，执行{" "}
                      <Text code>pip install -r requirements.txt</Text>。
                    </Text>
                  ),
                },
                {
                  title: "配置文件",
                  description: (
                    <span>
                      <Text code>cp config.example.toml config.toml</Text>
                      ，编辑 <Text code>config.toml</Text>：填写 ADS、飞书
                      Webhook（可选）、<Text code>data_dir</Text>、
                      <Text code>[[accounts]]</Text> 等。详见包内{" "}
                      <Text code>docs/</Text> 与 <Text code>README.md</Text>。
                    </span>
                  ),
                },
                {
                  title: "运行",
                  description: (
                    <span>
                      推荐统一入口：{" "}
                      <Text code>python run.py</Text> 或{" "}
                      <Text code>python run.py my_config.toml</Text>
                      。亦可直接运行 <Text code>scheduler_main.py</Text>。
                    </span>
                  ),
                },
              ]}
            />
          </div>

          <Divider />

          <div>
            <Title level={4}>域名与密钥（API）</Title>
            <Paragraph>
              Raven 通过内部 <Text code>ccxt_kit</Text> 与 ADS
              凭证体系连接交易所：配置里写的是{" "}
              <Text strong>ADS 侧密钥与环境</Text>，以及每个账户的{" "}
              <Text code>account_code</Text>；交易所的 AccessKey /
              Secret 等由 ADS 按账户下发，无需在{" "}
              <Text code>config.toml</Text> 里手写交易所 API Key。
            </Paragraph>

            <Title level={5}>1. ADS 环境与密钥</Title>
            <ul className="raven-doc-list">
              <li>
                <Text code>ads_domain</Text>：填{" "}
                <Text code>test</Text> 或 <Text code>prod</Text>
                ，分别对应 ADS 的测试环境与生产环境，需与你们下发的密钥所属环境一致。
              </li>
              <li>
                <Text code>ads_key</Text>、<Text code>ads_secret_cipher</Text>
                ：向团队 ADS / 账户凭证服务申请（与{" "}
                <Text code>ads_domain</Text> 匹配）。用途是认证并拉取各{" "}
                <Text code>[[accounts]]</Text> 中 <Text code>account_code</Text>{" "}
                对应的交易所 API 凭证。
              </li>
            </ul>

            <Title level={5}>2. 交易所 API 域名（可选）</Title>
            <Paragraph>
              若需走反代、专线或官方拆分的多域名，可在{" "}
              <Text code>config.toml</Text> 中配置{" "}
              <Text code>[exchange_api_urls.交易所名]</Text>（交易所名与{" "}
              <Text code>accounts.exchange</Text> 一致、小写），例如{" "}
              <Text code>{'url = "https://…"'}</Text> 或分别填写{" "}
              <Text code>spot</Text> / <Text code>swap</Text> /{" "}
              <Text code>rest</Text>。不配置时，CCXT 使用各所默认官方 API
              地址。
            </Paragraph>

            <Title level={5}>3. <Text code>[report]</Text>：{" "}
              <Text code>api_url</Text> 与 <Text code>api_token</Text>
            </Title>
            <Paragraph>
              用于 Raven 在生成日报等时机，把成交量、资产等数据<strong>推送到本系统</strong>（
              <Text code>trade_volume_reporter.py</Text>
              ）。需先在 <Text code>[report]</Text> 中设置{" "}
              <Text code>enabled = true</Text> 后，再填写下列两项。
            </Paragraph>

            <Paragraph>
              <Text strong>
                <Text code>report.api_url</Text>
              </Text>
            </Paragraph>
            <ul className="raven-doc-list">
              <li>
                <strong>含义</strong>：本系统<strong>HTTP 服务根地址</strong>
                （即 Gin 监听地址对外的访问入口），程序会在此后面拼接路径，例如{" "}
                <Text code>/api/v1/test</Text>、批量交易上报等接口。
              </li>
              <li>
                <strong>写法</strong>：生产环境默认示例为{" "}
                <Text code>https://profitsync.beaquanter.com/</Text>
                （与 <Text code>config.example.toml</Text> 一致）；本地调试可用{" "}
                <Text code>http://127.0.0.1:9080</Text> 等。<strong>不要</strong>带路径
                前缀（不要写成 <Text code>…/api</Text> 或{" "}
                <Text code>…/api/v1</Text>）。末尾多一个斜杠一般无影响（代码会去掉）。
              </li>
              <li>
                <strong>网络</strong>：必须保证<strong>运行 Raven 的机器</strong>
                能访问该地址（内网互通、防火墙、HTTPS 证书等）。公网部署时通常把
                Nginx/Caddy 反代到后端端口，此处填对外域名即可。
              </li>
            </ul>

            <Paragraph>
              <Text strong>
                <Text code>report.api_token</Text>
              </Text>
            </Paragraph>
            <ul className="raven-doc-list">
              <li>
                <strong>含义</strong>：本系统<strong>API Token</strong> 的
                字符串；请求时会放在 HTTP 头{" "}
                <Text code>X-API-Token</Text> 中，用于识别调用方并校验权限（与
                浏览器登录用的 JWT 不是同一种）。
              </li>
              <li>
                <strong>获取</strong>：Web 端<strong>管理员</strong>进入「
                Token 管理」→ 新建 Token，权限需包含能调用<strong>交易量上传</strong>
                相关接口（写入交易/批量数据）；创建成功后<strong>仅首次</strong>
                展示完整密钥，复制后写入 <Text code>config.toml</Text> 的{" "}
                <Text code>api_token</Text> 字段。
              </li>
              <li>
                <strong>安全</strong>：勿提交到 Git、勿贴到公开聊天；若泄露请在
                Token 管理中<strong>轮换或禁用</strong>后重新生成。
              </li>
            </ul>

            <Paragraph type="secondary">
              可选：<Text code>test_connection = true</Text>（默认）时，启动
              会在首次创建上报器前请求 <Text code>GET …/api/v1/test</Text>{" "}
              做连通性检查；失败则不会上报。调试时可关闭以跳过检测。
            </Paragraph>

            <Title level={5}>4. 飞书 Webhook</Title>
            <Paragraph style={{ marginBottom: 0 }}>
              <Text code>feishu_webhook</Text> 在飞书群「设置 → 群机器人 →
              添加机器人 → Webhook」中创建，将生成的 Hook 地址粘贴到配置即可；与交易所
              API 无关。
            </Paragraph>
          </div>

          <Divider />

          <div>
            <Title level={4}>
              <BookOutlined /> 功能摘要
            </Title>
            <List
              size="small"
              dataSource={[
                "定时成交量检查（默认约每 2 小时），无成交时可通过飞书报警。",
                "自动卖币：按配置的 token_coins 在约定时间卖出为 USDT（现货/合约流程见 README）。",
                "每日报告：成交统计、手续费、盈亏；支持历史 CSV 持久化。",
                "对冲组：多账户对冲资金统计（若配置了 hedge_groups）。",
                "可选：shell 脚本 start_monitor.sh / stop_monitor.sh 用于常驻进程。",
              ]}
              renderItem={(item) => <List.Item>{item}</List.Item>}
            />
          </div>

          <Divider />

          <div>
            <Title level={4}>包内文档</Title>
            <Paragraph>
              解压后请优先阅读：
            </Paragraph>
            <ul className="raven-doc-list">
              <li>
                <Text code>README.md</Text> — 总览与交易所支持说明
              </li>
              <li>
                <Text code>docs/快速开始.md</Text> — 分步配置与功能开关
              </li>
              <li>
                <Text code>docs/配置说明.md</Text>、
                <Text code>docs/使用指南.md</Text> — 进阶选项
              </li>
              <li>
                <Text code>config.example.toml</Text> — 配置模板与注释
              </li>
            </ul>
          </div>

          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            技术问题请以包内文档为准；与本 Web
            系统账号无关，需在运行环境中自行维护 Python 进程与配置文件。
          </Paragraph>
        </Space>
      </Card>
    </div>
  );
};

export default RavenDownload;
